import { useIntl } from 'react-intl'
import CircularProgress from '@mui/material/CircularProgress'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '@/store'
import {
	selectCartItemCount,
	selectCartTotal,
	selectIsCalculating,
	selectCalculateErrors,
	selectIsScheduleAvailable,
	selectStoreSettingsChangedError,
	selectOrderTypeAmountAlert,
	calculate,
} from '@/store/ticketSlice'
import { useFeatureFlag } from '@/providers/FeatureFlagProvider'
import { useLocationRef } from '@/contexts/RefContext'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatPrice } from '@/lib/formatPrice'
import { PATHS } from '@/router/paths'
import { CheckoutButtonRoot } from './CheckoutButton.styled'

export function CheckoutButton() {
	const intl = useIntl()
	const { t } = useFormatMessage()
	const navigate = useNavigate()
	const locationRef = useLocationRef()
	const dispatch = useAppDispatch()
	const quantityOnHandEnabled = useFeatureFlag('sto-quantity-on-hand', false)

	const itemCount = useAppSelector(selectCartItemCount)
	const cartTotal = useAppSelector(selectCartTotal)
	const isCalculating = useAppSelector(selectIsCalculating)
	const calculateErrors = useAppSelector(selectCalculateErrors)
	const isScheduleAvailable = useAppSelector(selectIsScheduleAvailable)
	const storeSettingsChangedError = useAppSelector(selectStoreSettingsChangedError)
	const amountAlert = useAppSelector(selectOrderTypeAmountAlert)

	const formattedTotal = formatPrice(cartTotal, intl.locale, 'USD')

	// AC4: amount violations keep button disabled; OrderTypeMinAlert banner communicates why.
	// No ErrorPopup — Figma has no popup state for this (TL decision 2026-07-07).
	const isAmountBlocked =
		amountAlert !== null &&
		(amountAlert.variant === 'below-min' || amountAlert.variant === 'above-max')

	// AC3: isCalculating is intentionally excluded — it renders a spinner, not a disabled button
	const isCheckoutDisabled =
		itemCount === 0 ||
		calculateErrors !== null ||
		!isScheduleAvailable ||
		storeSettingsChangedError !== null ||
		isAmountBlocked

	async function handleClick() {
		if (isCalculating || isCheckoutDisabled) return
		try {
			await dispatch(calculate({ quantityOnHandEnabled }))
			void navigate(PATHS.checkout(locationRef))
		} catch (err) {
			// TODO: STO-P2-S4-04 — sold-out warning page not yet implemented
			if (err instanceof Error && err.message === 'ITMOOSTK') {
				void navigate(PATHS.soldOutWarning(locationRef))
			}
		}
	}

	// AC3: calculating — spinner shown, button NOT disabled, aria-disabled="false"
	if (isCalculating) {
		return (
			<CheckoutButtonRoot
				isDisabled={false}
				isLoading={true}
				aria-disabled="false"
				onClick={() => void handleClick()}
				aria-label={t('component:cartActionsFooter:checkout:ariaLabel', {
					total: formattedTotal,
				})}
			>
				<CircularProgress size={16} color="inherit" />
				{t('component:cartActionsFooter:checkout:label', { total: formattedTotal })}
			</CheckoutButtonRoot>
		)
	}

	return (
		<CheckoutButtonRoot
			isDisabled={isCheckoutDisabled}
			isLoading={false}
			disabled={isCheckoutDisabled}
			aria-disabled={isCheckoutDisabled ? 'true' : 'false'}
			onClick={() => void handleClick()}
			aria-label={t('component:cartActionsFooter:checkout:ariaLabel', {
				total: formattedTotal,
			})}
		>
			{t('component:cartActionsFooter:checkout:label', { total: formattedTotal })}
		</CheckoutButtonRoot>
	)
}
